/**
 * Condition Grouping Utilities
 *
 * Pure functions for splitting a flat RouteGuard[] into AND-groups
 * and joining groups back into a flat array with logic connectors.
 *
 * Group computation algorithm:
 * - Iterate conditions; collect into current group while logic !== 'or'
 * - When logic === 'or' is found on conditions[i], that condition ends the
 *   current group; conditions[i+1] starts a new group.
 * - Evaluator semantics: (group0_c0 AND group0_c1) OR (group1_c0 AND ...)
 *
 * Example:
 *   conditions = [
 *     { key: "goal", op: "eq", val: "lose", logic: "and" },  // group 0
 *     { key: "age",  op: "gt", val: "18",   logic: "or" },   // group 0 END
 *     { key: "goal", op: "eq", val: "gain", logic: "and" },  // group 1
 *     { key: "gender", op: "eq", val: "male" },               // group 1
 *   ]
 *   splitIntoGroups → [[c0, c1], [c2, c3]]
 *   Evaluator: (goal==lose AND age>18) OR (goal==gain AND gender==male)
 *
 * @module v2-condition-editor
 */

import type { RouteGuard, ConditionLogic } from '@segmently/schema-models/v2';

/**
 * Minimal interface for items that have an optional logic connector.
 * Allows splitIntoGroups/joinGroups to work with RouteGuard or any
 * object that has the same connector field.
 */
export interface HasLogicConnector {
  logic?: ConditionLogic;
}

/**
 * Split a flat array of items (with optional logic fields) into AND-groups.
 *
 * An item with `logic === 'or'` ends the current group.
 * The next item starts a new group.
 *
 * @param items - Flat array with optional logic fields
 * @returns Array of groups; each group is an AND-collection
 */
export function splitIntoGroups<T extends HasLogicConnector>(items: T[]): T[][] {
  if (items.length === 0) return [[]];

  const groups: T[][] = [];
  let currentGroup: T[] = [];

  for (const item of items) {
    currentGroup.push(item);
    if (item.logic === 'or') {
      groups.push(currentGroup);
      currentGroup = [];
    }
  }

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}

/**
 * Join groups back into a flat array with correct logic connectors.
 *
 * Rules applied:
 * - Within a group: `logic` is stripped (defaults to 'and' when absent)
 * - Last item of each non-final group: `logic = 'or'` (group separator)
 * - Last item of the final group: `logic` is stripped (no next condition)
 *
 * @param groups - Array of AND-groups
 * @returns Flat RouteGuard[] ready for storage in Route.conditions
 */
export function joinGroups(groups: RouteGuard[][]): RouteGuard[] {
  const result: RouteGuard[] = [];

  for (let gi = 0; gi < groups.length; gi++) {
    const group = groups[gi];
    const isLastGroup = gi === groups.length - 1;

    for (let ci = 0; ci < group.length; ci++) {
      const isLastInGroup = ci === group.length - 1;

      // Build a clean copy without the source logic (we assign deliberately)
      const { logic: _stripped, ...rest } = group[ci];

      if (isLastInGroup && !isLastGroup) {
        // End of a non-final group → OR connector to next group
        result.push({ ...rest, logic: 'or' as ConditionLogic });
      } else {
        // Within group or end of final group → no logic needed (defaults to 'and')
        result.push({ ...rest });
      }
    }
  }

  return result;
}
